import React from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  MessageCircle,
  Globe,
  PhoneCall
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const WHATSAPP_NUMBER = "919819648166";
const WHATSAPP_MESSAGE = encodeURIComponent("Hello, I am interested in your export products.");
const PHONE = "+919819648166";
const PHONE_DISPLAY = "+91 9819648166";
const EMAIL = "enquiry@nirmalexport.com";

const ContactPage = () => {
  const primaryContacts = [
    {
      icon: PhoneCall,
      title: 'Call Us',
      value: PHONE_DISPLAY,
      description: 'Speak with our export team',
      link: `tel:${PHONE}`,
      color: 'primary',
      cta: 'Call Now',
      testid: 'contact-call-card'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp Us',
      value: PHONE_DISPLAY,
      description: 'Instant chat with our team',
      link: `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`,
      color: 'secondary',
      cta: 'Open WhatsApp',
      external: true,
      testid: 'contact-whatsapp-card'
    },
    {
      icon: Mail,
      title: 'Email Us',
      value: EMAIL,
      description: 'For detailed enquiries',
      link: `mailto:${EMAIL}`,
      color: 'accent',
      cta: 'Send Email',
      testid: 'contact-email-card'
    }
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-primary">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-accent font-medium text-sm tracking-wider uppercase">Contact Us</span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mt-3 mb-6">
              Let's Talk Business
            </h1>
            <p className="text-white/80 text-lg leading-relaxed">
              Reach us directly through call, WhatsApp, or email. Our export team is ready 
              to assist you with quotations, specifications, and shipment details.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Primary Contact Cards */}
      <section className="py-16 lg:py-20 -mt-16 relative z-10">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {primaryContacts.map((contact, index) => (
              <motion.a
                key={contact.title}
                href={contact.link}
                target={contact.external ? '_blank' : undefined}
                rel={contact.external ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -6 }}
                data-testid={contact.testid}
                className="block"
              >
                <Card className="h-full border-0 shadow-xl hover:shadow-2xl transition-shadow bg-white group">
                  <CardContent className="p-8 text-center">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center transition-transform group-hover:scale-110 ${
                      contact.color === 'primary' ? 'bg-primary/10' :
                      contact.color === 'secondary' ? 'bg-secondary/10' : 'bg-accent/10'
                    }`}>
                      <contact.icon className={`w-8 h-8 ${
                        contact.color === 'primary' ? 'text-primary' :
                        contact.color === 'secondary' ? 'text-secondary' : 'text-accent'
                      }`} />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-primary mb-1">
                      {contact.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      {contact.description}
                    </p>
                    <div className={`font-medium text-base mb-5 ${
                      contact.color === 'primary' ? 'text-primary' :
                      contact.color === 'secondary' ? 'text-secondary' : 'text-primary'
                    }`}>
                      {contact.value}
                    </div>
                    <Button 
                      className={`w-full font-semibold ${
                        contact.color === 'primary' ? 'btn-primary text-white' :
                        contact.color === 'secondary' ? 'btn-secondary text-white' : 
                        'bg-accent hover:bg-accent/90 text-primary'
                      }`}
                    >
                      {contact.cta}
                    </Button>
                  </CardContent>
                </Card>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Company Information */}
      <section className="py-16 lg:py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-secondary font-medium text-sm tracking-wider uppercase">Get in Touch</span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary mt-3">
                Company Information
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {/* Office Address */}
              <Card className="border-0 shadow-lg bg-surface h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-primary mb-2">
                    Office Address
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed" data-testid="office-address">
                    KWC-950, Kalamboli Steel Market,<br />
                    Kalamboli, Raigad,<br />
                    Maharashtra - 410218, India
                  </p>
                </CardContent>
              </Card>

              {/* Business Hours */}
              <Card className="border-0 shadow-lg bg-surface h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-secondary" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-primary mb-2">
                    Business Hours
                  </h3>
                  <div className="text-muted-foreground text-sm space-y-1">
                    <div>Mon - Sat</div>
                    <div className="font-medium text-primary">9:00 AM - 6:00 PM</div>
                    <div className="text-xs text-muted-foreground/70 mt-2">(Indian Standard Time)</div>
                  </div>
                </CardContent>
              </Card>

              {/* Website */}
              <Card className="border-0 shadow-lg bg-surface h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                    <Globe className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-primary mb-2">
                    Website
                  </h3>
                  <a 
                    href="https://nirmalexport.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-muted-foreground text-sm hover:text-secondary transition-colors font-medium"
                    data-testid="website-link"
                  >
                    www.nirmalexport.com
                  </a>
                </CardContent>
              </Card>

              {/* Direct Phone */}
              <Card className="border-0 shadow-lg bg-surface h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-primary mb-2">
                    Direct Line
                  </h3>
                  <a 
                    href={`tel:${PHONE}`}
                    className="text-muted-foreground text-sm hover:text-primary transition-colors font-medium block"
                    data-testid="direct-phone-link"
                  >
                    {PHONE_DISPLAY}
                  </a>
                  <p className="text-xs text-muted-foreground/70 mt-2">
                    Available during business hours
                  </p>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Big CTA Section */}
      <section className="py-16 bg-primary">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Ready to Connect?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Choose the option most convenient for you. We respond promptly to all enquiries.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href={`tel:${PHONE}`} data-testid="cta-call-btn">
                <Button 
                  size="lg" 
                  className="btn-secondary text-white font-semibold px-8 py-6 text-base"
                >
                  <PhoneCall className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-btn"
              >
                <Button 
                  size="lg" 
                  className="bg-[#25D366] hover:bg-[#20BA5C] text-white font-semibold px-8 py-6 text-base"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </a>
              <a href={`mailto:${EMAIL}`} data-testid="cta-email-btn">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 font-semibold px-8 py-6 text-base"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Email Us
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
